import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { AGENTS } from '../utils/agents';

const API = getApiUrl();

/**
 * V5.99.4 — useContextualAgent.
 *
 * Returns the AI agent persona (Sabril, Sophie, Léo, Manu, Théo, Clara) that
 * best matches the current route. Falls back gracefully to a hard-coded Sabril
 * (the "default" strategy agent) if the API is unreachable, so the UI never
 * breaks even without a network.
 */
const FALLBACK = {
  key: 'sabril',
  name: 'Sabril',
  role: 'Manager des agents · expertise hôtellerie-restauration',
  tagline: 'L’expertise de Sabril, une vision globale de votre établissement',
  color: 'from-amber-500 to-orange-600',
  avatar: '/brand/agents/sabril.webp?v=2',
};

let memoCache = null;
let inflight = null;

const routeFallback = (path, explicit) => {
  const modules = {
    stocks: 'sophie', inventaire: 'sophie', dlc: 'sophie', fournisseurs: 'sophie', commandes: 'sophie', livraisons: 'theo',
    fiches: 'leo', haccp: 'leo', hygiene: 'leo', 'recettes-couts': 'leo', 'fiches-techniques': 'leo', menus: 'leo', checklists: 'leo', communiquer: 'clara', 'relation-client': 'clara', 'social-media': 'clara',
    rh: 'manu', equipe: 'manu', planning: 'manu', pointage: 'manu', conges: 'manu', recrutement: 'manu', evaluation: 'manu', formation: 'manu', onboarding: 'manu', taches: 'manu', performance: 'manu',
    caisse: 'theo', ventes: 'theo', rentabilite: 'theo', facturation: 'theo', 'dashboard-ca': 'theo', 'mon-abonnement': 'theo',
    digital: 'clara', da: 'clara', 'reseaux-sociaux': 'clara', charte: 'clara', tendances: 'clara',
  };
  const module = path.split('/').filter(Boolean)[1];
  const key = explicit || modules[module] || 'sabril';
  return { ...FALLBACK, ...(AGENTS[key] || AGENTS.sabril), key, avatar: `/brand/agents/${key}.webp?v=2`, tagline: (AGENTS[key] || AGENTS.sabril).role };
};

export function useContextualAgent(explicitKey) {
  const location = useLocation();
  const cacheKey = explicitKey || location.pathname;
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = explicitKey || location.pathname;
    if (memoCache?.[cacheKey]) {
      setResolved({ cacheKey, agent: memoCache[cacheKey] });
      return;
    }
    (async () => {
      try {
        if (explicitKey) {
          const all = await fetchAllAgents();
          const found = (all || []).find((a) => a.key === explicitKey);
          if (!cancelled && found) { memoCache = { ...(memoCache || {}), [cacheKey]: found }; setResolved({ cacheKey, agent: found }); }
          return;
        }
        const r = await axios.get(`${API}/ai/agents/pick?path=${encodeURIComponent(location.pathname)}`, { withCredentials: true });
        if (!cancelled && r?.data?.key) {
          memoCache = { ...(memoCache || {}), [cacheKey]: r.data };
          setResolved({ cacheKey, agent: r.data });
        }
      } catch {
        // keep fallback
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, explicitKey]);

  return resolved?.cacheKey === cacheKey ? resolved.agent : memoCache?.[cacheKey] || routeFallback(location.pathname, explicitKey);
}

// Resolve all agents in one shot (cached). Used by the chat agent switcher.
let allAgentsCache = null;
export async function fetchAllAgents() {
  if (allAgentsCache) return allAgentsCache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await axios.get(`${API}/ai/agents`, { withCredentials: true });
      allAgentsCache = r?.data?.agents || [];
      return allAgentsCache;
    } catch {
      return [FALLBACK];
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
